import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { css, keyframes } from 'styled-components';
import { autoUpdate, useFloating } from '@floating-ui/react';
import { useTheme } from 'foundations-theming/utils';
import { ONBOARDING_COLOR } from '../constants/colors';
import { useStableElements } from '../utils/elementUtils';
import { usePulserContainer } from '../hooks/pulser';
import { useReplaceTargetBorder } from '../hooks/useReplaceTargetBorder';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const POPOVER_DEFAULT_Z_INDEX = 1111;
const CYCLE_DURATION = 7;
const fadeIn = keyframes(["from{opacity:0;}to{opacity:1;}"]);
const pulserAnimation = keyframes(["0%{opacity:0;transform:translate(-50%,-50%) scale(1.5) rotate(0turn);}8%{opacity:0.85;}78%{opacity:0.85;}89%{opacity:0;}100%{opacity:0;transform:translate(-50%,-50%) scale(1.5) rotate(3turn);}"]);
const PulserBorderElement = styled.div.withConfig({
  displayName: "UITourPulser__PulserBorderElement"
})(["position:absolute;border-radius:", "px;overflow:hidden;padding:", "px;-webkit-mask:linear-gradient(#fff 0 0) content-box,linear-gradient(#fff 0 0);-webkit-mask-composite:xor;mask-composite:exclude;pointer-events:none;", ";&::before{content:", ";background:conic-gradient( transparent 60deg,", " 180deg 240deg,transparent 340deg );position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);aspect-ratio:1;width:100%;", ";}"], p => p.$borderRadius, p => p.$borderSize, p => !p.$enablePulse ? `background: ${p.$borderColor};` : '', p => p.$enablePulse ? "''" : 'none', p => p.$borderColor, css(["animation:", " ", "s linear infinite;"], pulserAnimation, CYCLE_DURATION));
const PulserLayer = styled.div.withConfig({
  displayName: "UITourPulser__PulserLayer"
})(["", " position:fixed;inset:0;pointer-events:none;z-index:", ";"], p => p.$animate !== false && css(["animation:", " 0.3s ease-out forwards;"], fadeIn), p => p.$zIndex);
const pulserRects = {
  name: 'rects',
  fn({
    x,
    y,
    rects
  }) {
    return {
      x,
      y,
      data: Object.assign({}, rects.reference, {
        x,
        y
      })
    };
  }
};
const ModernBorder = ({
  borderColor,
  borderRadius,
  borderSize,
  enableBorder,
  enablePulse,
  padding,
  target
}) => {
  const {
    refs,
    middlewareData
  } = useFloating({
    elements: {
      reference: target
    },
    placement: 'top-start',
    strategy: 'fixed',
    middleware: [pulserRects],
    whileElementsMounted: autoUpdate
  });
  const {
    x = 0,
    y = 0,
    width = 0,
    height = 0
  } = (middlewareData === null || middlewareData === void 0 ? void 0 : middlewareData.rects) || {};
  useEffect(() => {
    if (refs.reference.current !== target) {
      refs.setPositionReference(target);
    }
  }, [refs, target]);
  const style = {
    top: y - padding,
    left: x - padding,
    width: width + padding * 2,
    height: height + padding * 2
  };
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx("span", {
      ref: refs.setFloating,
      style: {
        display: 'none'
      }
    }), enableBorder && /*#__PURE__*/_jsx(PulserBorderElement, {
      className: "tour-pulser-border",
      $borderRadius: borderRadius,
      $borderSize: borderSize,
      $borderColor: borderColor,
      $enablePulse: enablePulse,
      style: style
    })]
  });
};
const UITourPulserModern = ({
  attachTo,
  borderColor: borderColorProp,
  borderRadius = 8,
  borderSize = 1.25,
  enableBorder = true,
  enablePulse = true,
  pulserPadding = 4,
  pulserZIndex = POPOVER_DEFAULT_Z_INDEX,
  replaceTargetBorder = true,
  target
}) => {
  const theme = useTheme();
  const borderColor = borderColorProp !== null && borderColorProp !== void 0 ? borderColorProp : theme.color[ONBOARDING_COLOR];
  const [popoverAnchor, setPopoverAnchor] = useState(null);
  const pulserContainer = usePulserContainer(popoverAnchor);
  const pulserTargets = useStableElements(attachTo || target);
  const replacedStyles = useReplaceTargetBorder({
    enabled: replaceTargetBorder,
    targets: pulserTargets
  });

  // When an input border was replaced, match the input's original dimensions
  // instead of using the pulser's defaults
  let effectivePadding = pulserPadding;
  let effectiveBorderSize = borderSize;
  let effectiveBorderRadius = borderRadius;
  if (replacedStyles) {
    effectivePadding = 0;
    effectiveBorderSize = replacedStyles.borderWidth;
    effectiveBorderRadius = replacedStyles.borderRadius;
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx("span", {
      ref: setPopoverAnchor,
      "aria-hidden": true,
      style: {
        display: 'none'
      }
    }), pulserContainer && /*#__PURE__*/createPortal( /*#__PURE__*/_jsx(PulserLayer, {
      className: "tour-pulser-modern",
      $zIndex: pulserZIndex,
      $animate: !replacedStyles,
      children: pulserTargets.map((pulserTarget, index) => /*#__PURE__*/_jsx(ModernBorder, {
        borderColor: borderColor,
        borderRadius: effectiveBorderRadius,
        borderSize: effectiveBorderSize,
        enableBorder: enableBorder,
        enablePulse: enablePulse,
        padding: effectivePadding,
        target: pulserTarget
      }, pulserTarget.id || pulserTarget.getAttribute('data-onboarding') || index))
    }), pulserContainer)]
  });
};
export default UITourPulserModern;